import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import type { RoomInfo } from "../lib/matrix-client";

interface SOPPreviewProps {
  open: boolean;
  onClose: () => void;
  markdown: string;
  onPublish: (roomId: string) => void;
  publishing?: boolean;
  /** The user's personal VoxDoc room. Single locked publish target. */
  targetRoom: RoomInfo | null;
  loadingTarget?: boolean;
  /** Optional human-friendly description of why the target can't be resolved. */
  targetError?: string | null;
  title?: string;
}

export function SOPPreview({
  open,
  onClose,
  markdown,
  onPublish,
  publishing,
  targetRoom,
  loadingTarget,
  targetError,
  title = "Structured SOP Preview",
}: SOPPreviewProps) {
  const canPublish = !!targetRoom && !publishing && !loadingTarget;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="prose prose-sm dark:prose-invert max-w-none border rounded-lg p-4 bg-muted/30">
          <pre className="whitespace-pre-wrap text-sm font-mono">
            {markdown}
          </pre>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground whitespace-nowrap">Publishing to:</span>
          {loadingTarget ? (
            <span className="text-muted-foreground">Locating your VoxDoc room…</span>
          ) : targetRoom ? (
            <span className="font-medium">{targetRoom.name}</span>
          ) : (
            <span className="text-destructive">
              {targetError || "No VoxDoc room available"}
            </span>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Use &amp; Edit
          </Button>
          <Button
            onClick={() => targetRoom && onPublish(targetRoom.id)}
            disabled={!canPublish}
          >
            {publishing ? "Publishing..." : "Save to Matrix"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
